import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DashboardHeader from '../../components/DashboardHeader';
import ErrorMessage from '../../components/ErrorMessage';
import ErrorToast from '../../components/ErrorToast';
import HansePayLoader from '../../components/HansePayLoader';
import FlagIcon from '../../components/FlagIcon';
import { DownloadIcon, HistoryIcon, SendIcon, ChevronDownIcon } from '../../assets/icons';
import * as transactionsActions from '../../store/actions/transactions';
import { formatPaymentPurpose } from '../../utils/recipientHelpers';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { formatNumberWithDecimalLayout } from '../../utils/userPreferencesHelpers';

const CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-sm)]';
const IN_TRANSIT_STATUSES = ['Executing', 'Processing', 'Pending'];
const SETTLED_STATUSES = ['Completed'];
const FAILED_STATUSES = ['Failed', 'Cancelled', 'Rejected'];

const STATUS_DISPLAY = {
  Completed: { label: 'Settled', color: 'var(--color-success)' },
  Pending: { label: 'In Transit', color: '#1d4ed8' },
  Executing: { label: 'In Transit', color: '#1d4ed8' },
  Processing: { label: 'In Transit', color: '#1d4ed8' },
  Failed: { label: 'Failed', color: 'var(--color-error)' },
  Cancelled: { label: 'Failed', color: 'var(--color-error)' },
  Rejected: { label: 'Failed', color: 'var(--color-error)' },
};

function toTransactionList(transactions) {
  return Array.isArray(transactions) ? transactions : Object.values(transactions || {});
}

function isIncomingTransaction(transaction) {
  const direction = String(transaction.direction || transaction.flow || transaction.type || '').toLowerCase();
  if (['incoming', 'inbound', 'credit', 'receive', 'received', 'deposit'].some((token) => direction.includes(token))) {
    return true;
  }
  const purpose = String(transaction.paymentPurpose || '').toLowerCase();
  return ['incoming', 'deposit', 'top_up', 'topup', 'credit'].some((token) => purpose.includes(token));
}

function getStatusDisplay(status) {
  return STATUS_DISPLAY[status] || { label: status || '—', color: 'var(--text-tertiary)' };
}

function matchesStatusGroup(status, group) {
  if (group === 'all') return true;
  if (group === 'settled') return SETTLED_STATUSES.includes(status);
  if (group === 'in-transit') return IN_TRANSIT_STATUSES.includes(status);
  if (group === 'failed') return FAILED_STATUSES.includes(status);
  return true;
}

function formatRowDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRowSubtitle(transaction) {
  const parts = [];
  if (transaction.paymentReference) parts.push(transaction.paymentReference);
  else if (transaction.paymentPurpose) parts.push(formatPaymentPurpose(transaction.paymentPurpose));
  const dateLabel = formatRowDate(transaction.started || transaction.dateAdded);
  if (dateLabel) parts.push(dateLabel);
  return parts.join(' · ') || '—';
}

function formatMonthLabel(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function calculateDuration(startDate, endDate) {
  if (!startDate || !endDate) return '-';
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-';
  const diffMs = end - start;
  if (diffMs < 0) return '-';
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  return `${minutes} min ${seconds} sec`;
}

function groupByMonth(transactions) {
  const groups = new Map();
  transactions.forEach((transaction) => {
    const key = formatMonthLabel(transaction.started || transaction.dateAdded);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(transaction);
  });
  return Array.from(groups.entries());
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[12.5px] font-medium font-[family-name:var(--font-ui)] transition-colors ${
        active
          ? 'bg-[var(--white)] text-[var(--n800)] shadow-[0_1px_3px_rgba(11,25,41,0.1)]'
          : 'bg-transparent text-[var(--text-tertiary)]'
      }`}
    >
      {children}
    </button>
  );
}

FilterPill.propTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

function StatCard({ label, value, detail }) {
  return (
    <div className={`${CARD_CLASS} px-[22px] py-5`}>
      <div className='mb-2.5 text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)]'>{label}</div>
      <div className='mb-1 font-display text-[32px] font-light leading-none tracking-[-0.02em] text-[var(--n900)]'>{value}</div>
      <div className='text-xs text-[var(--text-secondary)]'>{detail}</div>
    </div>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  detail: PropTypes.string.isRequired,
};

function StatusPill({ status }) {
  const { label, color } = getStatusDisplay(status);
  return (
    <span
      className='inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-[0.04em]'
      style={{ color, background: `color-mix(in oklab, ${color} 10%, transparent)` }}
    >
      <span className='h-[5px] w-[5px] rounded-full' style={{ background: color }} />
      {label}
    </span>
  );
}

StatusPill.propTypes = {
  status: PropTypes.string,
};

function TransactionRow({ transaction, onOpen, preferences }) {
  const incoming = isIncomingTransaction(transaction);
  const amountClass = incoming ? 'text-[var(--color-success)]' : 'text-[var(--n900)]';
  const value = Number(transaction.amountIn) || 0;
  const formatted = formatNumberWithDecimalLayout(value, preferences);
  const amountLabel = `${incoming ? '+' : '−'}${formatted} €`;

  return (
    <button
      type='button'
      onClick={() => onOpen(transaction.requestId)}
      className='grid w-full grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4 border-b border-[var(--color-border)] px-[22px] py-3.5 text-left transition-colors last:border-b-0 hover:bg-[var(--off)]'
    >
      {incoming ? (
        <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.1)] text-[var(--color-success)]'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M12 4v12M6 10l6 6 6-6M4 20h16' />
          </svg>
        </div>
      ) : (
        <div className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--off)]'>
          <FlagIcon currencyCode={transaction.quoteCurrency} className='h-5 w-7 rounded-sm object-cover' />
        </div>
      )}

      <div className='min-w-0'>
        <div className='flex items-center gap-1.5 text-[13.5px] font-medium text-[var(--n900)]'>
          <span className='truncate'>{transaction.recipient?.name || '—'}</span>
          {incoming && (
            <span className='shrink-0 rounded-full bg-[rgba(46,107,173,0.1)] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--n400)]'>
              IN
            </span>
          )}
        </div>
        <p className='mt-0.5 truncate text-xs text-[var(--text-tertiary)]'>{formatRowSubtitle(transaction)}</p>
      </div>

      <StatusPill status={transaction.status} />

      <div className={`min-w-[130px] text-right text-sm font-medium tabular-nums font-[family-name:var(--font-ui)] ${amountClass}`}>
        {amountLabel}
      </div>

      <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' className='shrink-0' aria-hidden='true'>
        <path d='M9 18l6-6-6-6' />
      </svg>
    </button>
  );
}

TransactionRow.propTypes = {
  transaction: PropTypes.shape({
    requestId: PropTypes.string,
    recipient: PropTypes.shape({ name: PropTypes.string }),
    quoteCurrency: PropTypes.string,
    status: PropTypes.string,
    amountIn: PropTypes.number,
    started: PropTypes.string,
    dateAdded: PropTypes.string,
    paymentReference: PropTypes.string,
    paymentPurpose: PropTypes.string,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
  preferences: PropTypes.object,
};

function TransactionHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { gettingTransactions, getTransactionsError, transactions } = useSelector((state) => state.transactions);
  const { preferences, formatCurrency, formatNumber } = useNumberFormat();

  const formatEuroTotal = (amount) => `${formatNumber(Number(amount) || 0)} €`;

  const [directionFilter, setDirectionFilter] = useState('all');
  const [statusGroupFilter, setStatusGroupFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const exportDropdownRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(transactionsActions.getTransactions());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allTransactions = useMemo(
    () =>
      toTransactionList(transactions)
        .filter((t) => t.started || t.dateAdded)
        .sort((a, b) => new Date(b.started || b.dateAdded) - new Date(a.started || a.dateAdded)),
    [transactions],
  );

  const directionCounts = useMemo(() => {
    const sent = allTransactions.filter((t) => !isIncomingTransaction(t)).length;
    const received = allTransactions.length - sent;
    return { all: allTransactions.length, sent, received };
  }, [allTransactions]);

  const filteredTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allTransactions.filter((transaction) => {
      const incoming = isIncomingTransaction(transaction);
      if (directionFilter === 'sent' && incoming) return false;
      if (directionFilter === 'received' && !incoming) return false;
      if (!matchesStatusGroup(transaction.status, statusGroupFilter)) return false;

      if (!query) return true;
      const recipient = transaction.recipient?.name?.toLowerCase() || '';
      const reference = transaction.paymentReference?.toLowerCase() || '';
      const purpose = formatPaymentPurpose(transaction.paymentPurpose).toLowerCase();
      const requestId = String(transaction.requestId || '').toLowerCase();
      const pair = transaction.currencyPair?.toLowerCase() || '';
      return recipient.includes(query) || reference.includes(query) || purpose.includes(query) || requestId.includes(query) || pair.includes(query);
    });
  }, [allTransactions, directionFilter, statusGroupFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [directionFilter, statusGroupFilter, searchQuery]);

  const totalItems = filteredTransactions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  const monthGroups = useMemo(() => groupByMonth(paginatedTransactions), [paginatedTransactions]);

  const viewStats = useMemo(() => {
    const sentItems = filteredTransactions.filter((t) => !isIncomingTransaction(t));
    const receivedItems = filteredTransactions.filter((t) => isIncomingTransaction(t));
    const inProgress = filteredTransactions.filter((t) => IN_TRANSIT_STATUSES.includes(t.status)).length;
    const totalSent = sentItems.reduce((sum, t) => sum + (Number(t.amountIn) || 0), 0);
    const currencyCount = new Set(sentItems.map((t) => t.quoteCurrency).filter(Boolean)).size;
    return {
      count: filteredTransactions.length,
      totalSent,
      receivedCount: receivedItems.length,
      inProgress,
      currencyCount,
    };
  }, [filteredTransactions]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleExportCSV = () => {
    if (filteredTransactions.length === 0) return;

    const csvContent = [
      ['Recipient', 'Status', 'Amount In', 'Amount Out', 'Payment (Reference | Document | Purpose)', 'Total Fees', 'Started', 'Duration', 'Currency Pair'],
      ...filteredTransactions.map((transaction) => [
        transaction.recipient?.name || '-',
        transaction.status,
        formatCurrency(transaction.amountIn, transaction.baseCurrency),
        formatCurrency(transaction.amountOut, transaction.quoteCurrency),
        [transaction.paymentReference || '-', transaction.paymentDocument || '-', formatPaymentPurpose(transaction.paymentPurpose) || '-'].join(' | '),
        formatCurrency(transaction.totalFee, transaction.baseCurrency),
        formatDate(transaction.started),
        calculateDuration(transaction.started, transaction.finished),
        transaction.currencyPair,
      ]),
    ]
      .map((row) => row.map((field) => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const dateString = new Date().toISOString().split('T')[0];
    const fileName = `transactions_${dateString}.csv`;

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setExportDropdownOpen(false);
  };

  const handleExportPDF = () => {
    if (filteredTransactions.length === 0) return;

    const printWindow = window.open('', '_blank');
    const today = new Date();
    const dateString = today.toISOString().split('T')[0];

    const rows = filteredTransactions
      .map(
        (transaction) => `
        <tr>
          <td>${transaction.recipient?.name || '-'}</td>
          <td>${getStatusDisplay(transaction.status).label}</td>
          <td class="amount">${formatCurrency(transaction.amountIn, transaction.baseCurrency)}</td>
          <td class="amount">${formatCurrency(transaction.amountOut, transaction.quoteCurrency)}</td>
          <td>${transaction.paymentReference || '—'}</td>
          <td>${formatDate(transaction.started)}</td>
        </tr>`,
      )
      .join('');

    const htmlContent = `<!DOCTYPE html>
      <html>
        <head>
          <title>Transaction Statement - ${dateString}</title>
          <style>
            body { font-family: sans-serif; margin: 0; padding: 24px; color: #0b1929; }
            h1 { font-size: 24px; margin: 0 0 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border-bottom: 1px solid #e8e7e8; padding: 12px; text-align: left; font-size: 13px; }
            th { background: #f6f9fc; }
            .amount { text-align: right; font-weight: 600; }
          </style>
        </head>
        <body>
          <h1>All transactions</h1>
          <p>Exported ${filteredTransactions.length} transactions · ${dateString}</p>
          <table>
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Status</th>
                <th>Amount in</th>
                <th>Amount out</th>
                <th>Reference</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
    setExportDropdownOpen(false);
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, '…', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '…', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '…', currentPage, '…', totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Layout hideHeader mainPaddingClassName='px-6 py-9 sm:px-11'>
      <div className='flex flex-col gap-7'>
        <div className='flex items-start justify-between gap-6'>
          <DashboardHeader navLabel='Transactions' title='All transactions' />
          <div className='relative mt-1.5 shrink-0' ref={exportDropdownRef}>
            <button
              type='button'
              onClick={() => setExportDropdownOpen((open) => !open)}
              className='inline-flex h-10 items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-4 text-sm font-medium text-[var(--n800)] transition-colors hover:bg-[var(--off)]'
            >
              <DownloadIcon className='h-4 w-4' />
              Export
              <ChevronDownIcon className='h-4 w-4' />
            </button>
            {exportDropdownOpen && (
              <div className='absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]'>
                <button type='button' onClick={handleExportCSV} className='block w-full px-4 py-2.5 text-left text-sm text-[var(--n800)] hover:bg-[var(--off)]'>
                  Export as CSV
                </button>
                <button type='button' onClick={handleExportPDF} className='block w-full px-4 py-2.5 text-left text-sm text-[var(--n800)] hover:bg-[var(--off)]'>
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>

        <div className='flex flex-col gap-6'>
          <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
            <StatCard label='Transactions' value={viewStats.count} detail='In current view' />
            <StatCard
              label='Total sent'
              value={formatEuroTotal(viewStats.totalSent)}
              detail={viewStats.currencyCount > 0 ? `Converted from ${viewStats.currencyCount} currencies` : 'No outgoing transfers'}
            />
            <StatCard label='Received' value={viewStats.receivedCount} detail='Incoming this view' />
            <StatCard label='In progress' value={viewStats.inProgress} detail='Awaiting settlement' />
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <div className='flex gap-0.5 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-[3px]'>
              <FilterPill active={directionFilter === 'all'} onClick={() => setDirectionFilter('all')}>
                All<span className='text-[10.5px] font-semibold text-[var(--text-tertiary)] opacity-85'>{directionCounts.all}</span>
              </FilterPill>
              <FilterPill active={directionFilter === 'sent'} onClick={() => setDirectionFilter('sent')}>
                Sent<span className='text-[10.5px] font-semibold text-[var(--text-tertiary)] opacity-85'>{directionCounts.sent}</span>
              </FilterPill>
              <FilterPill active={directionFilter === 'received'} onClick={() => setDirectionFilter('received')}>
                Received<span className='text-[10.5px] font-semibold text-[var(--text-tertiary)] opacity-85'>{directionCounts.received}</span>
              </FilterPill>
            </div>

            <div className='flex gap-0.5 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-[3px]'>
              <FilterPill active={statusGroupFilter === 'all'} onClick={() => setStatusGroupFilter('all')}>
                All
              </FilterPill>
              <FilterPill active={statusGroupFilter === 'settled'} onClick={() => setStatusGroupFilter('settled')}>
                Settled
              </FilterPill>
              <FilterPill active={statusGroupFilter === 'in-transit'} onClick={() => setStatusGroupFilter('in-transit')}>
                In Transit
              </FilterPill>
              <FilterPill active={statusGroupFilter === 'failed'} onClick={() => setStatusGroupFilter('failed')}>
                Failed
              </FilterPill>
            </div>

            <div className='relative min-w-[220px] flex-1'>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.8' className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2' aria-hidden='true'>
                <circle cx='11' cy='11' r='8' />
                <path d='M21 21l-4.35-4.35' />
              </svg>
              <input
                type='search'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search by description, reference, recipient…'
                className='w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] py-2 pl-[34px] pr-3.5 text-[13px] text-[var(--n900)] outline-none placeholder:text-[var(--text-tertiary)] focus:ring-2 focus:ring-[var(--n200)]'
              />
            </div>
          </div>

          {gettingTransactions && <HansePayLoader variant='inline' />}

          {!gettingTransactions && getTransactionsError && (
            <ErrorMessage error={getTransactionsError} onDismiss={() => dispatch(transactionsActions.getTransactions())} />
          )}

          {!gettingTransactions && !getTransactionsError && totalItems === 0 && (
            <div className={`${CARD_CLASS} flex flex-col items-center px-6 py-14 text-center`}>
              <HistoryIcon className='mb-4 h-14 w-14 text-[var(--text-tertiary)] opacity-50' />
              <h3 className='mb-2 text-lg font-medium text-[var(--n800)]'>No transactions found</h3>
              <p className='max-w-md text-sm text-[var(--text-secondary)]'>
                {allTransactions.length === 0
                  ? "You haven't made any transactions yet. Start by sending money to your recipients."
                  : 'No transactions match your current filters. Try adjusting your search or filters.'}
              </p>
              {allTransactions.length === 0 && (
                <button
                  type='button'
                  onClick={() => navigate('/send-money')}
                  className='mt-6 inline-flex items-center gap-2 rounded-lg bg-[var(--n800)] px-5 py-2.5 text-sm font-medium text-white shadow-[var(--shadow-cta)] hover:opacity-90'
                >
                  <SendIcon className='h-4 w-4' />
                  Send money
                </button>
              )}
            </div>
          )}

          {!gettingTransactions && !getTransactionsError && totalItems > 0 && (
            <div className='flex flex-col gap-5'>
              {monthGroups.map(([monthLabel, monthTransactions]) => {
                const monthSentTotal = monthTransactions
                  .filter((t) => !isIncomingTransaction(t))
                  .reduce((sum, t) => sum + (Number(t.amountIn) || 0), 0);

                return (
                  <div key={monthLabel}>
                    <div className='mb-2.5 flex items-baseline justify-between px-1'>
                      <div className='text-[11px] font-semibold uppercase tracking-[0.13em] text-[var(--text-tertiary)]'>{monthLabel}</div>
                      {monthSentTotal > 0 && (
                        <div className='text-[11.5px] text-[var(--text-tertiary)]'>
                          Sent <strong className='font-medium text-[var(--n800)] font-[family-name:var(--font-ui)]'>{formatEuroTotal(monthSentTotal)}</strong>
                        </div>
                      )}
                    </div>
                    <div className={`${CARD_CLASS} overflow-hidden`}>
                      {monthTransactions.map((transaction) => (
                        <TransactionRow
                          key={transaction.requestId}
                          transaction={transaction}
                          preferences={preferences}
                          onOpen={(requestId) => navigate(`/transaction-history/${requestId}`)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              <div className='flex flex-wrap items-center justify-between gap-3 px-1'>
                <p className='text-xs tabular-nums text-[var(--text-tertiary)]'>
                  Showing <strong className='font-medium text-[var(--n800)]'>{startItem}–{endItem}</strong> of{' '}
                  <strong className='font-medium text-[var(--n800)]'>{totalItems}</strong> transactions
                </p>
                <div className='flex items-center gap-1'>
                  <button
                    type='button'
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                      <path d='M15 18l-6-6 6-6' />
                    </svg>
                    Prev
                  </button>
                  {pageNumbers.map((page) =>
                    page === '…' ? (
                      <span key='pagination-ellipsis' className='px-1 text-sm text-[var(--text-tertiary)]'>
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type='button'
                        onClick={() => handlePageChange(page)}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2.5 text-[12.5px] font-medium tabular-nums ${
                          currentPage === page
                            ? 'border border-[var(--n900)] bg-[var(--n900)] text-white'
                            : 'border border-[var(--color-border)] bg-[var(--white)] text-[var(--n800)]'
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    type='button'
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    Next
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                      <path d='M9 18l6-6-6-6' />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ErrorToast error={getTransactionsError} />
    </Layout>
  );
}

export default TransactionHistory;
